import {
	dehydrate,
	type DehydratedState,
	hydrate,
	keepPreviousData,
	QueryCache,
	QueryClient,
	VueQueryPlugin,
	type VueQueryPluginOptions,
} from "@tanstack/vue-query";

import { describeApiError } from "@/utils/api-error.ts";

export default defineNuxtPlugin({
	name: "project-query-client",
	dependsOn: ["pinia"],
	setup(nuxt) {
		const state = useState<DehydratedState | null>("vue-query");
		const toastsStore = useToastsStore();
		const { addToast } = toastsStore;

		const THREE_MIN = 3 * 60 * 1000;
		const FIFTEEN_MIN = 15 * 60 * 1000;

		const queryClient = new QueryClient({
			defaultOptions: {
				queries: {
					placeholderData: keepPreviousData,
					staleTime: FIFTEEN_MIN,
				},
			},
			queryCache: new QueryCache({
				onError(error) {
					if (import.meta.client) {
						addToast({
							title: "Error",
							description: describeApiError(error),
							type: "foreground",
							variant: "negative",
						});
					} else {
						console.error(error);
					}
				},
			}),
		});

		const options: VueQueryPluginOptions = { queryClient };

		nuxt.vueApp.use(VueQueryPlugin, options);

		if (import.meta.server) {
			nuxt.hooks.hook("app:rendered", () => {
				const dehydrated = dehydrate(queryClient);

				// Normalize volatile timestamps before sending to client to up to three minutes in the past
				for (const query of dehydrated.queries) {
					query.state.dataUpdatedAt = Math.floor(query.state.dataUpdatedAt / THREE_MIN) * THREE_MIN;
					query.state.errorUpdatedAt =
						Math.floor(query.state.errorUpdatedAt / THREE_MIN) * THREE_MIN;
					query.dehydratedAt = Math.floor((query.dehydratedAt ?? 0) / THREE_MIN) * THREE_MIN;
				}
				state.value = dehydrated;
			});
		}

		if (import.meta.client) {
			hydrate(queryClient, state.value);
		}
	},
});
